import { Upload, Expand, X } from "lucide-react";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import type { DraftMomentPhoto } from "../use-hub-store";

interface SwapPhotoCardProps {
  avatarUrl?: string;
  avatarName: string;
  imageUrl?: string;
  imageAlt: string;
  photo?: DraftMomentPhoto | null;
  needsUpload?: boolean;
  isConverting?: boolean;
  onFileSelect?: (files: FileList | null) => void;
  onRemovePhoto?: () => void;
  onExpand?: (e: React.MouseEvent) => void;
  onClick?: () => void;
  style?: React.CSSProperties;
  className?: string;
}

export function SwapPhotoCard({
  avatarUrl,
  avatarName,
  imageUrl,
  imageAlt,
  photo,
  needsUpload = false,
  isConverting = false,
  onFileSelect,
  onRemovePhoto,
  onExpand,
  onClick,
  style,
  className,
}: SwapPhotoCardProps) {
  const hasImage = !!imageUrl;
  const hasPhotoPreview = !!photo;

  return (
    <div
      onClick={onClick}
      className={className}
      style={style}
    >
      <div className="absolute left-4 top-4 z-10">
        <UserAvatar
          imageUrl={avatarUrl}
          name={avatarName}
          size={48}
          showBorder
        />
      </div>

      {hasImage ? (
        <>
          <img
            src={imageUrl}
            alt={imageAlt}
            className="h-full w-full object-cover"
          />
          {onExpand && (
            <button
              type="button"
              onClick={onExpand}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 shadow-md transition-colors hover:bg-white"
            >
              <Expand className="h-5 w-5 text-gray-700" />
            </button>
          )}
        </>
      ) : hasPhotoPreview && needsUpload ? (
        <>
          <img
            src={photo.preview}
            alt="Your swap photo"
            className="h-full w-full object-cover"
          />
          {onRemovePhoto && (
            <button
              type="button"
              onClick={onRemovePhoto}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-sm transition-colors hover:bg-white"
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </>
      ) : needsUpload && onFileSelect ? (
        <label className="flex h-full w-full cursor-pointer flex-col items-center justify-center bg-[#f8f8f8]">
          <Upload className="h-8 w-8 text-gray-400" />
          <span className="mt-2 text-base text-gray-500">
            {isConverting ? "Converting..." : "Upload photo"}
          </span>
          <input
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
            onChange={(e) => onFileSelect(e.target.files)}
            className="hidden"
            disabled={isConverting}
          />
        </label>
      ) : (
        <div className="h-full w-full bg-gray-100" />
      )}
    </div>
  );
}
